import { Box, SxProps } from '@mui/system';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  label?: string | null;
  embedUrl: string;
  sx?: SxProps;
  labelSx?: SxProps;
  videoClassName?: string;
  labelClassName?: string;
}

const isVideoFileUrl = (url: string) =>
  url.split('?')[0]?.split('#')[0]?.toLowerCase().endsWith('.mp4') ||
  url.split('?')[0]?.split('#')[0]?.toLowerCase().endsWith('.mov') ||
  url.split('?')[0]?.split('#')[0]?.toLowerCase().endsWith('.webm');
const isYoutubeUrl = (url: string) =>
  url.startsWith('https://youtube.com/watch?v=') ||
  url.startsWith('https://www.youtube.com/watch?v=') ||
  url.startsWith('https://www.youtube.com/embed/') ||
  url.startsWith('https://youtube.com/embed/') ||
  url.startsWith('https://youtu.be/') ||
  url.startsWith('https://www.youtu.be/');
const isVimeoUrl = (url: string) => url.includes('vimeo.com');

export const isEmbeddableUrl = (url: string) =>
  isVideoFileUrl(url) || isYoutubeUrl(url) || isVimeoUrl(url);

const youtubeUrlToEmbedUrl = (url: string) => {
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url.split('?v=').pop()?.split('&')[0] || '';
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/').pop()?.split('?')[0] || '';
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

const vimeoUrlToEmbedUrl = (url: string) => {
  const query = url.split('?')[1] || '';
  const matchEvent = /vimeo\.com\/event\/(\d+)\/(?:embed\/)?([a-zA-Z0-9]+)$/i.exec(url); // if event (livestream) url: from c -> https://vimeo.com/event/4193249/embed/bb633b6f40
  if (matchEvent !== null) {
    const videoId = matchEvent[1];
    const hash = matchEvent[2];
    return `https://vimeo.com/event/${videoId}/embed/${hash}?${query}`;
  }
  // Handle Vimeo Showcase URLs: https://vimeo.com/showcase/11854400?video=1114179927
  if (/vimeo\.com\/showcase\/\d+/.test(url)) {
    const params = new URLSearchParams(url.split('?')[1] || '');
    const videoId = params.get('video');
    if (videoId) {
      return `https://player.vimeo.com/video/${videoId}?${query}`;
    }
  }
  const matchVideo = /vimeo\.com\/.*?(\d+)/.exec(url); // extract video id
  if (matchVideo !== null) {
    const videoId = matchVideo[1];
    return `https://player.vimeo.com/video/${videoId}?${query}`;
  } else {
    return url;
  }
};

export default function LtVideo({
  label,
  labelSx,
  embedUrl,
  sx,
  videoClassName,
  labelClassName,
}: Props) {
  const [wrapperBox, setWrapperBoxRef] = useState<HTMLDivElement>();
  const [overrideVideoHeight, setOverrideVideoHeight] = useState<number | undefined>(undefined);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    // to make sure thumbnails are loaded and displayed (mainly safari)
    if (videoRef.current) videoRef.current.load();
  }, []);

  const videoElement = useMemo(() => {
    const videoStyles: CSSProperties = {
      borderRadius: '8px',
      overflow: 'hidden',
    };
    if (isYoutubeUrl(embedUrl)) {
      const src = youtubeUrlToEmbedUrl(embedUrl);
      return (
        <iframe
          title={label ?? undefined}
          src={src}
          allowFullScreen
          className={videoClassName}
          style={{
            ...videoStyles,
            width: '100%',
            height: overrideVideoHeight || undefined,
          }}
        />
      );
    }
    if (isVimeoUrl(embedUrl)) {
      const src = vimeoUrlToEmbedUrl(embedUrl);
      return (
        <iframe
          title={label ?? undefined}
          src={src}
          // frameborder='0'
          allow='autoplay; fullscreen; picture-in-picture'
          // allowfullscreen
          className={videoClassName}
          style={{
            ...videoStyles,
            width: '100%',
            height: overrideVideoHeight || undefined,
          }}
        />
      );
    }
    return (
      <video
        controls
        playsInline
        preload='metadata'
        ref={videoRef}
        style={{ ...videoStyles, maxWidth: '100%' }}
        className={videoClassName}
      >
        <source src={embedUrl} />
        Your browser does not support the video tag.
      </video>
    );
  }, [embedUrl, label, overrideVideoHeight, videoClassName]);

  // Resize observer to set the video height to 16:9 ratio
  useEffect(() => {
    if (wrapperBox) {
      const observer = new ResizeObserver(entries => {
        // Use requestAnimationFrame to avoid layout thrashing
        window.requestAnimationFrame(() => {
          if (entries[0]) {
            setOverrideVideoHeight((entries[0].contentRect.width * 9) / 16);
          }
        });
      });

      observer.observe(wrapperBox);

      // Cleanup observer on unmount
      return () => observer.disconnect();
    }
  }, [wrapperBox]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, ...sx }}>
      <Box ref={setWrapperBoxRef} sx={{ width: '100%' }}>
        {videoElement}
      </Box>
      {label && (
        <Box
          className={labelClassName}
          textAlign='center'
          sx={{ ...(labelSx || {}), typography: 'h5' }}
        >
          {label}
        </Box>
      )}
    </Box>
  );
}
